import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaArrowRight } from 'react-icons/fa';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";

// Define animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const Hero = () => {
  const [key, setKey] = useState(0);
  const particlesContainerRef = useRef(null);

  const particlesInit = useCallback(async engine => {
    await loadSlim(engine);
  }, []);

  const particlesLoaded = useCallback(async container => {
    console.log("Particles container loaded", container);
    particlesContainerRef.current = container;
  }, []);

  // Restart particles every 5 minutes to prevent animation speed issues
  useEffect(() => {
    const resetInterval = setInterval(() => {
      if (particlesContainerRef.current) {
        // Force reload the particles component
        setKey(prevKey => prevKey + 1);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(resetInterval);
  }, []);

  return (
    <HeroContainer>
      <ParticlesBackground
        key={key}
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={{
          fullScreen: { enable: false },
          fpsLimit: 60,
          particles: {
            color: {
              value: ["#FF8C00", "#2E8B57", "#FFFFFF"],
            },
            links: {
              color: "#FF8C00",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1
            },
            collisions: {
              enable: true,
            },
            move: {
              direction: "none",
              enable: true,
              outModes: {
                default: "bounce"
              },
              random: false,
              speed: 1.2,
              straight: false,
              attract: {
                enable: false,
                rotateX: 600,
                rotateY: 1200
              }
            },
            number: {
              density: {
                enable: true,
                area: 800
              },
              value: 60
            },
            opacity: {
              value: 0.5,
              random: true,
              anim: {
                enable: true,
                speed: 0.8,
                opacity_min: 0.1,
                sync: false
              }
            },
            shape: {
              type: ["circle", "triangle", "polygon"],
              polygon: {
                nb_sides: 6
              }
            },
            size: {
              value: { min: 1, max: 5 },
              random: true,
              anim: {
                enable: true,
                speed: 1,
                size_min: 0.1,
                sync: false
              }
            }
          },
          interactivity: {
            detectsOn: "canvas",
            events: {
              onhover: {
                enable: true,
                mode: "repulse"
              },
              onclick: {
                enable: true,
                mode: "push"
              },
              resize: true
            },
            modes: {
              grab: {
                distance: 140,
                links: {
                  opacity: 1
                }
              },
              bubble: {
                distance: 400,
                size: 40,
                duration: 2,
                opacity: 8,
                speed: 3
              },
              repulse: {
                distance: 100,
                duration: 0.4
              },
              push: {
                particles_nb: 4
              },
              remove: {
                particles_nb: 2
              }
            }
          },
          retina_detect: true
        }}
      />

      <HeroOverlay />

      <HeroContent>
        <HeroText>
          <h1>Professional Site Clearing & Demolition Services</h1>
          <p>Precision demolition, efficient site clearing, and expert property preparation for your next construction project. Service-based business – we come to you! Serving Crescent City and Putnam County, Florida with over 15 years of experience.</p>
          <HeroButtons>
            <Link to="/contact#quote-form" className="btn primary">Get Your Free Quote <FaArrowRight /></Link>
            <Link to="/services" className="btn secondary">Explore Our Services</Link>
          </HeroButtons>
        </HeroText>
        <HeroStats>
          <StatItem>
            <StatNumber>15+</StatNumber>
            <StatText>Years Experience</StatText>
          </StatItem>
          <StatItem>
            <StatNumber>500+</StatNumber>
            <StatText>Projects Completed</StatText>
          </StatItem>
          <StatItem>
            <StatNumber>100%</StatNumber>
            <StatText>Satisfaction Rate</StatText>
          </StatItem>
        </HeroStats>
      </HeroContent>
    </HeroContainer>
  );
};

const ParticlesBackground = styled(Particles)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 1;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 140, 0, 0.2) 0%,
    rgba(0, 0, 0, 0.6) 100%
  );
  z-index: 2;
`;

const HeroContainer = styled.section`
  height: 100vh;
  min-height: 700px;
  background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.9)), 
    url('/images/hero-bg.jpg') center/cover no-repeat;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 3;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 50px;
`;

const HeroText = styled.div`
  max-width: 750px;
  
  h1 {
    font-size: 5.6rem;
    line-height: 1.1;
    margin-bottom: 2.5rem;
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 2s ease-out;
    
    span {
      color: var(--primary-color);
    }
  }
  
  p {
    font-size: 1.8rem;
    margin-bottom: 3.5rem;
    max-width: 600px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    animation: ${fadeIn} 2s ease-out 0.6s both;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 4rem;
    }
    
    p {
      font-size: 1.6rem;
    }
  }
  
  @media (max-width: 480px) {
    h1 {
      font-size: 3.2rem;
    }
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 5rem;
  animation: ${fadeIn} 2s ease-out 1s both;
  
  .btn {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.6rem;
    padding: 1.5rem 3rem;
    border-radius: 5px;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: all 0.3s ease;
    
    svg {
      font-size: 1.4rem;
      transition: transform 0.3s ease;
    }
    
    &:hover svg {
      transform: translateX(5px);
    }
  }
  
  .primary {
    background-color: var(--primary-color);
    color: white;
    font-weight: 700;
    
    &:hover {
      background-color: var(--secondary-color);
      color: white;
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    }
  }
  
  .secondary {
    background-color: transparent;
    color: white;
    border: 2px solid var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color);
      color: white;
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
    }
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1.5rem;
    
    .btn {
      width: 100%;
      justify-content: center;
    }
  }
`;

const HeroStats = styled.div`
  display: flex;
  gap: 5rem;
  margin-top: 2rem;
  animation: ${fadeIn} 1s ease-out 0.9s both;
  
  @media (max-width: 768px) {
    gap: 3rem;
  }
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 2rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  animation: ${float} 10s ease-in-out infinite;
`;

const StatNumber = styled.div`
  font-size: 4.8rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
  animation: ${pulse} 8s ease-in-out infinite;
  
  @media (max-width: 768px) {
    font-size: 3.6rem;
  }
`;

const StatText = styled.div`
  font-size: 1.6rem;
  color: white;
  opacity: 0.8;
`;

export default Hero; 